import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import "./CaseStudiesCard.css";

const asText = (value, fallback = "") => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return fallback;
};

const normalizeTags = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string" || typeof item === "number") {
          return String(item);
        }
        return asText(item?.title ?? item?.name ?? item?.label);
      })
      .filter(Boolean)
      .slice(0, 4);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 4);
  }
  return [];
};

const ScrollLink = ({ to, children, ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
};

const CaseStudiesCard = ({
  caseStudy,
  CaseStudies,
  sequence = 1,
  isActive = false,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  // Supports both the new `caseStudy` prop and your previous `CaseStudies` prop.
  const project = caseStudy ?? CaseStudies ?? {};
  const title = asText(project.title ?? project.name, "Axiomos Project");
  const description = asText(
    project.descriptionsmall ??
      project.shortDescription ??
      project.summary ??
      project.description ??
      project.overview,
    "A focused digital solution designed to improve operations and create measurable business value.",
  );
  const image = asText(
    project.photo ??
      project.image ??
      project.imageUrl ??
      project.coverImage ??
      project.heroImage ??
      project.thumbnail ??
      project.image1 ??
      project.imageSrc ??
      project.img,
  );
  const category = asText(
    project.industry ?? project.category ?? project.type,
    "Case study",
  );
  const projectName = asText(project.name);
  const client = asText(
    project.client ??
      project.company ??
      project.organization ??
      (projectName !== title ? projectName : ""),
  );
  const externalLink = asText(
    project.link ?? project.liveUrl ?? project.website ?? project.projectUrl,
  );
  const tags = normalizeTags(
    project.technologies ??
      project.technology ??
      project.techStack ??
      project.tags,
  );
  const fallback =
    title.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "AX";
  const projectRoute = project.id ? `/projects/${project.id}` : "/projects";
  const sequenceNumber = Number.isFinite(Number(sequence)) ? Number(sequence) : 1;

  const mediaContent = (
    <>
      {image && !imageFailed ? (
        <img
          src={image}
          alt={`${title} project preview`}
          loading={isActive ? "eager" : "lazy"}
          decoding="async"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="case-project-card__image-fallback" aria-hidden="true">
          {fallback}
        </span>
      )}

      <span className="case-project-card__media-overlay" aria-hidden="true" />
      <span className="case-project-card__category">{category}</span>
      <span className="case-project-card__sequence" aria-hidden="true">
        Project / {String(sequenceNumber).padStart(2, "0")}
      </span>

      {externalLink && (
        <span className="case-project-card__live-label">
          View live project
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" />
        </span>
      )}
    </>
  );

  return (
    <article className="case-project-card">
      <div className="case-project-card__layout">
        {externalLink ? (
          <a
            className="case-project-card__media"
            href={externalLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open the live ${title} project in a new tab`}
          >
            {mediaContent}
          </a>
        ) : (
          <div className="case-project-card__media">{mediaContent}</div>
        )}

        <div className="case-project-card__content">
          <div className="case-project-card__content-top">
            <span>Selected case study</span>
            {client && <p>{client}</p>}
          </div>

          <h3>{title}</h3>
          <p className="case-project-card__description">{description}</p>

          {tags.length > 0 && (
            <ul className="case-project-card__tags" aria-label="Technologies used">
              {tags.map((tag, index) => (
                <li key={`${tag}-${index}`}>{tag}</li>
              ))}
            </ul>
          )}

          <div className="case-project-card__actions">
            <ScrollLink
              className="case-project-card__internal-link"
              to={projectRoute}
            >
              Read case study
              <span aria-hidden="true">
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </ScrollLink>

            {externalLink && (
              <a
                className="case-project-card__external-link"
                href={externalLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Live project
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  aria-hidden="true"
                />
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default CaseStudiesCard;
